// ─── Floow Cart Recommendations ─────────────────────────────────────────────
// Custom Element im Cart-Drawer. Quelle mit Priorität:
//   1. source="bundle": Inhalt kommt serverseitig aus dem Metafeld
//      custom.bundle des ersten Warenkorb-Produkts – hier wird nur der
//      Swiper initialisiert.
//   2. source="recommendations": lädt die Section
//      "floow-cart-recommendations" über den Product-Recommendations-
//      Endpoint (Search & Discovery Fallback).
// Aktualisiert sich bei jeder Warenkorbänderung; der Swiper wird erst
// initialisiert, wenn der Drawer sichtbar ist (sonst falsche Maße).
if (!window.customElements.get("floow-cart-recommendations")) {
  // v2 – merged sources (bundle + S&D + fallback), server content kept on 404
  class FloowCartRecommendations extends HTMLElement {
    static observedAttributes = ["product-id", "source"];

    connectedCallback() {
      this._abortController = new AbortController();
      const signal = this._abortController.signal;

      // Nach jeder Warenkorbänderung aktualisieren – der Drawer wird per
      // Morph neu gerendert, wodurch Inhalt/Attribute wechseln können.
      document.addEventListener(
        "cart:change",
        () =>
          setTimeout(() => {
            // Warenkorbinhalt hat sich geändert – Empfehlungen neu zusammenführen
            this._loadedFor = null;
            this._refresh();
          }, 150),
        { signal }
      );

      // Swiper erst initialisieren, wenn der Drawer wirklich sichtbar ist
      document.addEventListener(
        "dialog:after-show",
        (event) => {
          if (event.target.contains && event.target.contains(this)) this._ensureSlider();
        },
        { signal }
      );

      // Robustes Fallback: sobald das Element sichtbar wird (egal wodurch),
      // Swiper initialisieren
      this._visibilityObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this._ensureSlider();
      });
      this._visibilityObserver.observe(this);

      this._refresh();
    }

    disconnectedCallback() {
      this._abortController.abort();
      this._visibilityObserver?.disconnect();
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue !== null && oldValue !== newValue) this._refresh();
    }

    get _source() {
      return this.getAttribute("source") || "recommendations";
    }

    _refresh() {
      // Serverseitig mitgelieferten Inhalt (Bundle/Fallback) sofort anzeigen –
      // nach einem Drawer-Morph können die DOM-Knoten ersetzt worden sein.
      this._swiper = null;
      this._ensureSlider();

      // Immer zusätzlich die zusammengeführte Liste (Bundle + Search &
      // Discovery + Fallback) über den Endpoint laden und Inhalt ersetzen.
      this._load();
    }

    async _load() {
      const productId = this.getAttribute("product-id");
      const url = this.getAttribute("recommendations-url");
      if (!productId || !url) return;

      const loadKey = productId;
      if (this._loadedFor === loadKey && this.querySelector(".floow-cart-recs__inner")) {
        this._ensureSlider();
        return;
      }
      this._loadingFor = loadKey;

      try {
        const response = await fetch(
          `${url}?section_id=floow-cart-recommendations&product_id=${productId}&limit=8&intent=related`
        );
        if (!response.ok) {
          // Keine Empfehlungen vom Endpoint (404) – serverseitig gerenderten
          // Inhalt (Bundle/Fallback) behalten.
          this._loadedFor = loadKey;
          this.hidden = !this.querySelector(".floow-cart-recs__inner");
          return;
        }

        const doc = new DOMParser().parseFromString(await response.text(), "text/html");
        const inner = doc.querySelector(".floow-cart-recs__inner");

        // Antwort veraltet (Warenkorb hat sich inzwischen geändert)?
        if (this._loadingFor !== loadKey) return;

        this.innerHTML = "";
        this._swiper = null;

        if (!inner) {
          this.hidden = true;
          this._loadedFor = loadKey;
          return;
        }

        // Inerte Script-Reste des Slider-Snippets entfernen – initialisiert
        // wird der Swiper hier im Element.
        inner.querySelectorAll('script:not([type="application/json"])').forEach((script) => script.remove());

        this.hidden = false;
        this.appendChild(inner);
        this._loadedFor = loadKey;
        this._ensureSlider();
      } catch (error) {
        /* noop */
      }
    }

    _ensureSlider() {
      // Nur initialisieren, wenn Inhalt vorhanden und Element sichtbar ist
      if (!this.querySelector(".floow-cart-recs__inner")) return;
      if (this.offsetWidth === 0) return;

      if (this._swiper && this.contains(this._swiper.el)) {
        this._swiper.update();
        return;
      }

      this._initSlider();
    }

    async _initSlider() {
      const sliderEl = this.querySelector('[class*="slider-"]');
      if (!sliderEl) return;

      const slideCount = sliderEl.querySelectorAll(".swiper-slide").length;
      if (slideCount === 0) return;

      const { default: Swiper } = await import(
        "https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.mjs"
      );

      if (this._swiper) {
        try {
          this._swiper.destroy(true, true);
        } catch (error) {
          /* noop */
        }
        this._swiper = null;
      }

      this._swiper = new Swiper(sliderEl, {
        slidesPerView: 1,
        spaceBetween: 20,
        speed: 600,
        autoHeight: true,
        loop: slideCount > 1,
        navigation: {
          nextEl: this.querySelector('[class*="right-arrow-"]'),
          prevEl: this.querySelector('[class*="left-arrow-"]')
        }
      });

      if (slideCount <= 1) {
        this.querySelectorAll(".floow-arrow").forEach((arrow) => (arrow.style.display = "none"));
      }
    }
  }

  window.customElements.define("floow-cart-recommendations", FloowCartRecommendations);
}
